import { h } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'

const DEBOUNCE_MS = 250

/**
 * MemberPicker — search + multi/single-select person list with chips.
 *
 * Matches ERB chat-member-select / chat-member-chips look (chat.scss).
 *
 * Props:
 *   selected    — Person[] currently selected
 *   onChange    — (persons: Person[]) => void
 *   excludeIds  — Number[] person ids to hide (e.g. current members, self)
 *   multiple    — Boolean (default true). When false, selecting replaces selection.
 *   disabled    — Boolean
 */
export function MemberPicker({
  selected = [],
  onChange,
  excludeIds = [],
  multiple = true,
  disabled = false,
}) {
  const query   = useSignal('')
  const results = useSignal([])
  const loading = useSignal(false)
  const empty   = useSignal(false)
  const timer   = useRef(null)
  const exclude = new Set(excludeIds.map(Number))

  useEffect(() => {
    load('')
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, []) // eslint-disable-line

  function load(q) {
    loading.value = true
    chatApi.persons.list(q || undefined).then(async res => {
      if (!res.ok) return
      const data = await res.json
      const list = Array.isArray(data) ? data : (data.persons ?? [])
      results.value = list.filter(p => !exclude.has(Number(p.id)))
      empty.value = results.value.length === 0
    }).catch(() => {
      results.value = []
      empty.value = true
    }).finally(() => { loading.value = false })
  }

  function onSearchInput(e) {
    const q = e.target.value
    query.value = q
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => load(q.trim()), DEBOUNCE_MS)
  }

  function isSelected(id) {
    return selected.some(p => Number(p.id) === Number(id))
  }

  function toggle(person) {
    if (disabled) return
    if (multiple) {
      if (isSelected(person.id)) {
        onChange(selected.filter(p => Number(p.id) !== Number(person.id)))
      } else {
        onChange([...selected, person])
      }
    } else {
      onChange(isSelected(person.id) ? [] : [person])
    }
  }

  function removeChip(person) {
    if (disabled) return
    onChange(selected.filter(p => Number(p.id) !== Number(person.id)))
  }

  return (
    <div>
      {selected.length > 0 && (
        <div class="chat-member-chips">
          {selected.map(p => (
            <span key={p.id} class="chat-member-chip">
              <PersonAvatar person={p} size={20} />
              <span class="chat-member-chip-name">{p.name}</span>
              {!disabled && (
                <button
                  type="button"
                  class="chat-member-chip-remove"
                  aria-label={t('chat.rooms.remove_member')}
                  onClick={() => removeChip(p)}
                >
                  <i class="bi bi-x" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div class="chat-member-search-wrap">
        <i class="bi bi-search chat-member-search-icon" />
        <input
          type="text"
          class="chat-member-search-input"
          placeholder={t('chat.rooms.search_members')}
          value={query.value}
          onInput={onSearchInput}
          autocomplete="off"
          disabled={disabled}
        />
      </div>

      <div class="chat-member-select border rounded">
        {loading.value && results.value.length === 0 ? (
          <p class="text-muted small mb-0 p-3">
            <span class="spinner-border spinner-border-sm me-2" role="status" />
          </p>
        ) : empty.value ? (
          <p class="text-muted small mb-0 p-3">
            {query.value.trim()
              ? t('chat.rooms.search_no_results')
              : t('chat.rooms.no_superadmins')}
          </p>
        ) : (
          results.value.map(person => {
            const checked = isSelected(person.id)
            return (
              <div
                key={person.id}
                class="chat-member-select-item"
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => toggle(person)}
                onKeyDown={e => {
                  if (disabled) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggle(person)
                  }
                }}
              >
                <input
                  class="form-check-input"
                  type={multiple ? 'checkbox' : 'radio'}
                  checked={checked}
                  readOnly
                  tabIndex={-1}
                  disabled={disabled}
                  aria-hidden="true"
                />
                <label class="chat-member-label">
                  <PersonAvatar person={person} size={28} />
                  <span class="small chat-member-name">{person.name}</span>
                </label>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

function PersonAvatar({ person, size = 28 }) {
  if (person.avatar_url) {
    return (
      <img
        src={person.avatar_url}
        class="chat-room-avatar"
        width={size}
        height={size}
        alt={person.name || ''}
      />
    )
  }
  const initial = (person.name || '?').slice(0, 1).toUpperCase()
  return (
    <div
      class="chat-room-avatar chat-room-avatar--initials chat-room-avatar--direct"
      style={{ width: size, height: size, fontSize: '0.7rem' }}
    >
      {initial}
    </div>
  )
}
